import type { ReportGroup } from './groupReports';
import { extractPostTitle } from './extractPostTitle';

interface Props {
  group: ReportGroup;
  variant: 'table' | 'feed';
  onOpen: (postId: string) => void;
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const hrs = Math.round(diffMs / (1000 * 60 * 60));
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function assigneeLabel(g: ReportGroup): string {
  const s = g.assigneeSummary;
  switch (s.kind) {
    case 'unassigned':
      return 'unassigned';
    case 'single':
      return `@${s.moderatorUsername ?? s.moderatorId}`;
    case 'multiple':
      return `${s.count} assignees`;
    case 'partial':
      return `partially unassigned (${s.count})`;
  }
}

export default function ReportGroupRow({ group, variant, onOpen }: Props) {
  const primaryReason = group.reasons[0];
  const count = group.reports.length;
  // group.post may be null when the underlying post was deleted. Fall back to
  // postId and mark the row visually so moderators know the post is unavailable.
  const postDeleted = group.post === null;
  const title = extractPostTitle(group.post, group.postId);
  const authorHandle = group.post?.username
    ? `@${group.post.username}`
    : null;

  if (variant === 'table') {
    return (
      <div
        data-testid="report-group-row"
        className="Moderation__Row Moderation__Row--table"
        role="button"
        aria-label={`Open reports for ${title}`}
        onClick={() => onOpen(group.postId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(group.postId);
          }
        }}
        tabIndex={0}
      >
        <div className="Moderation__Row__Categories">
          {group.categories.map((c) => (
            <span key={c} className={`Moderation__Chip Moderation__Chip--${c}`}>
              {c}
            </span>
          ))}
        </div>
        <div className="Moderation__Row__Main">
          <strong>{primaryReason}</strong>
          {count > 1 && (
            <span className="Moderation__Row__Badge">{count} reports</span>
          )}
          <span className="Moderation__Row__Title">{title}</span>
          {authorHandle && (
            <span className="Moderation__Row__Author">{authorHandle}</span>
          )}
          {postDeleted && (
            <span
              className="Moderation__Row__DeletedBadge"
              data-testid="post-deleted-badge"
            >
              post deleted
            </span>
          )}
        </div>
        <div className="Moderation__Row__Reporter">
          {group.reporters.slice(0, 2).map((r) => `@${r}`).join(', ')}
          {group.reporters.length > 2 && ` +${group.reporters.length - 2}`}
        </div>
        <div className="Moderation__Row__Assignee">{assigneeLabel(group)}</div>
        <div className="Moderation__Row__When">
          {formatRelative(group.groupCreatedAt)}
        </div>
      </div>
    );
  }

  // feed variant
  return (
    <div
      data-testid="report-group-row"
      className="Moderation__Row Moderation__Row--feed"
      onClick={() => onOpen(group.postId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(group.postId);
        }
      }}
      tabIndex={0}
      role="button"
    >
      <div className="Moderation__Row__Header">
        {group.categories.map((c) => (
          <span key={c} className={`Moderation__Chip Moderation__Chip--${c}`}>
            {c}
          </span>
        ))}
        <strong>{primaryReason}</strong>
        {count > 1 && (
          <span className="Moderation__Row__Badge">{count} reports</span>
        )}
      </div>
      <div className="Moderation__Row__Body">
        <span className="Moderation__Row__Title">{title}</span>
        {authorHandle && (
          <span className="Moderation__Row__Author"> · {authorHandle}</span>
        )}
        {postDeleted && (
          <span
            className="Moderation__Row__DeletedBadge"
            data-testid="post-deleted-badge"
          >
            post deleted
          </span>
        )}
      </div>
      <div className="Moderation__Row__Footer">
        <span>
          Reported by {group.reporters.slice(0, 2).map((r) => `@${r}`).join(', ')}
          {group.reporters.length > 2 && ` +${group.reporters.length - 2}`}
        </span>
        <span>· {assigneeLabel(group)}</span>
        <span>· {formatRelative(group.groupCreatedAt)}</span>
      </div>
    </div>
  );
}
